import { useState, useCallback } from 'react';
import { createHasSomeMonth } from '../../utils/planner';

const usePlannerFetcher = (fetcher: (months: Date[]) => Promise<void>, loadingLazy?: boolean) => {
    const [loadedMonths, setLoadedMonths] = useState<Date[] | null>(null);
    const [loading, setLoading] = useState(false);
    const isLoaded = useCallback(
        (month: Date) => {
            if (!loadedMonths) {
                return false;
            }

            return createHasSomeMonth(loadedMonths)(month);
        },
        [loadedMonths]
    );

    const loadMore = useCallback(
        async (months: Date[]) => {
            if (loading || loadingLazy) {
                return;
            }
            // we only want to fetch months that aren't loaded yet
            const corrected = months.filter(month => !isLoaded(month));

            if (!corrected.length) {
                return;
            }
            setLoading(true);

            await fetcher(corrected);

            setLoadedMonths(current => [...(current ?? []), ...months]);
            setLoading(false);
        },
        [fetcher, isLoaded, loading, loadingLazy]
    );

    const invalidate = useCallback(() => setLoadedMonths(null), []);

    return [
        { loadedMonths, loading },
        { isLoaded, invalidate, loadMore },
    ] as const;
};

export default usePlannerFetcher;
